import { ApiEndpoints } from '../../../../constants/endpoints/apiEndpoints.ts';
import { ActivityEndpoints } from '../../../../constants/endpoints/activityEndpoints.ts';
import { CloseButton, CreateFieldHelperReturn, SelectOption, useStandardToast } from '@invara-dev/react-base';
import {
  EditActivityUpdateModel,
  EditActivityViewModel
} from '../../../../features/activities/editActivity/editActivityForm/schema/editActivitySchema';
import { useEffect, useMemo, useRef, useState } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { stringIsNullOrEmpty } from '../../../../util/helpers/stringHelpers';
import { ClickableText } from '../../../../components/Common/ClickableText';
import { ActivityBookingFlagsModel } from '../../api/activityStatusModels';
import { validateTravelDates } from '../../../../features/activities/utils/validateTravelDates';
import { Box, Text } from '@chakra-ui/react';
import { getTotalNights } from '../../../../util/dates/dateUtils';
import { SelectedTravellerPill } from '../../../../features/activities/travellers/selectedTravellerPill';
import { useTravellerInputState } from '../../../../features/activities/travellers/useTravellerInputState';
import { PersonalCompanyToggle } from './components/personalCompanyToggle';
import { CompanyContactsSearchField } from '../../../../components/SharedFields/activities/CompanyContactsSearchField';
import { CompanyContactType } from '../../../../enums/crm/CompanyContactType';
import { ReconciliationEditStatuses } from '../../../../constants/activityStatusGroups.ts';
import ActivityStatus from '../../../../enums/activities/ActivityStatus.ts';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '../../../../constants/queryKeys';
import { useActivityQueryInvalidator } from '../useActivityQueryInvalidator';
import { useCompanyRelatedCompanyQuery } from '../../../../features/company/edit/apis/useCompanyRelatedCompanyQuery';
import { useCompanyDivisionBookingOptionsQuery } from '../../../../features/company/edit/apis/useCompanyDivisionBookingOptionsQuery';
import { HotelBookingType } from '../../../../globalModels/common/hotelBookingType';

export const useEditActivityFormFields = (
  activityId: number | undefined,
  fieldHelper: CreateFieldHelperReturn<EditActivityUpdateModel>,
  viewModel: EditActivityViewModel | undefined,
  formMethods: UseFormReturn<Partial<EditActivityUpdateModel>>,
  isNewActivityForm: boolean,
  activityStatus?: ActivityBookingFlagsModel,
  isDisabled?: boolean,
  approvalEnabled?: boolean
) => {
  const [companyId, hotelBookingType, cityId, checkInDate, checkOutDate, companyConfirmationId] = useWatch({
    control: formMethods.control,
    name: [
      'CompanyId',
      'HotelBookingType',
      'PreferredCityId',
      'CheckInDate',
      'CheckOutDate',
      'PreferredHotelId',
      'CompanyConfirmationId'
    ]
  });

  const companyRelatedCompanies = useCompanyRelatedCompanyQuery(companyId);
  const divisionQuery = useCompanyDivisionBookingOptionsQuery(companyId);
  const companyDivisionId = useWatch({ control: formMethods.control, name: 'CompanyDivisionId' });
  const divisions = divisionQuery.data ?? [];
  const previousCompanyId = useRef(companyId);
  const previousDivisionId = useRef(viewModel?.CompanyDivisionId);
  const initializedViewModelId = useRef(viewModel?.Id);

  useEffect(() => {
    if (viewModel?.Id != null && initializedViewModelId.current !== viewModel.Id) {
      initializedViewModelId.current = viewModel.Id;
      previousCompanyId.current = viewModel.CompanyId;
      previousDivisionId.current = viewModel.CompanyDivisionId;
    }
  }, [viewModel]);

  useEffect(() => {
    if (previousCompanyId.current === companyId) {
      return;
    }
    previousCompanyId.current = companyId;
    previousDivisionId.current = undefined;
    formMethods.setValue('CompanyDivisionId', undefined, { shouldDirty: true });
  }, [companyId, formMethods]);

  useEffect(() => {
    if (hotelBookingType === HotelBookingType.Personal) {
      if (companyDivisionId != null) {
        formMethods.setValue('CompanyDivisionId', undefined, { shouldDirty: true });
      }
      return;
    }
    if (divisions.length === 1 && companyDivisionId == null) {
      formMethods.setValue('CompanyDivisionId', divisions[0].Id, { shouldDirty: true });
    }
  }, [companyDivisionId, divisions, formMethods, hotelBookingType]);

  useEffect(() => {
    if (isNewActivityForm) {
      return;
    }
    if (previousDivisionId.current === companyDivisionId) {
      return;
    }
    previousDivisionId.current = companyDivisionId;
    const controlledRequirementIds = new Set(
      divisions.flatMap((division) => division.ReportingRequirementValues.map((value) => value.ReportingRequirementId))
    );
    const selectedPresets = new Map(
      divisions
        .find((division) => division.Id === companyDivisionId)
        ?.ReportingRequirementValues.map((value) => [value.ReportingRequirementId, value.Value]) ?? []
    );
    const currentRequirements = formMethods.getValues('ReportingRequirements') ?? [];
    formMethods.setValue(
      'ReportingRequirements',
      currentRequirements.map((requirement) =>
        controlledRequirementIds.has(requirement.ReportingRequirementId)
          ? { ...requirement, Value: selectedPresets.get(requirement.ReportingRequirementId) ?? '' }
          : requirement
      ),
      { shouldDirty: true }
    );
  }, [companyDivisionId, divisions, formMethods, isNewActivityForm]);
  const queryClient = useQueryClient();
  const queryInvalidator = useActivityQueryInvalidator(activityId ?? 0);
  const toast = useStandardToast();

  const [cityHotelFreeText, setCityHotelFreeText] = useState(!stringIsNullOrEmpty(viewModel?.PreferredCityFreeText));

  const { onNewTravellerCreated, initialTravelers } = useTravellerInputState<EditActivityUpdateModel>(
    formMethods,
    viewModel?.InitialTravellerSelectOptions ?? [],
    'TravelerIds'
  );

  return useMemo(() => {
    const bookingTypeLocked = isDisabled || activityStatus?.HasItinerary === true;

    return [
      fieldHelper.hiddenInput('CheckOutDate'),
      fieldHelper.hiddenInput('CheckInDate'),
      fieldHelper
        .selectInputQueryItems('Status', {
          searchUrl: ApiEndpoints.selectOptions.activities.activityStatus(activityId)
        })
        .withInputProps({ disableSort: true, disabled: isDisabled })
        .withValidation({ required: {} })
        .isNumeric(),
      fieldHelper
        .textInput('NumberOfRooms')
        .withInputProps({ min: 1, type: 'number' })
        .isNumeric()
        .withValidation({ required: isNewActivityForm ? {} : undefined }),
      fieldHelper
        .selectInputQueryItems('Priority', { searchUrl: ApiEndpoints.selectOptions.enum('ActivityPriorityLevel') })
        .withInputProps({ disabled: isDisabled })
        .withValidation({ required: {} })
        .isNumeric(),
      fieldHelper
        .selectInputQueryItems('CompanyId', { searchUrl: ApiEndpoints.selectOptions.companies.list(false) })
        .withValidation({ required: {} })
        .withInputAddons({
          inputRightAddon: <PersonalCompanyToggle formMethods={formMethods} isDisabled={bookingTypeLocked} />
        })
        .withInputProps({
          isClearable: !activityStatus?.HasItinerary || ReconciliationEditStatuses.includes(activityStatus?.Status),
          readOnly: activityStatus?.HasItinerary && !ReconciliationEditStatuses.includes(activityStatus?.Status)
        })
        .isNumeric(),

      fieldHelper
        .selectInput('CompanyDivisionId', {
          items: [
            ...(viewModel?.InitialCompanyDivisionSelectOption &&
            !divisions.some((division) => division.Id === Number(viewModel.InitialCompanyDivisionSelectOption?.Value))
              ? [viewModel.InitialCompanyDivisionSelectOption]
              : []),
            ...divisions.map((division): SelectOption => ({ Label: division.Name, Value: division.Id }))
          ]
        })
        .withLabel('Division')
        .withRenderIf(
          () =>
            hotelBookingType !== HotelBookingType.Personal &&
            (divisions.length > 1 ||
              (companyDivisionId != null && !divisions.some((division) => division.Id === companyDivisionId)))
        )
        .withValidation({
          required: divisions.length > 0 && hotelBookingType !== HotelBookingType.Personal ? {} : undefined
        })
        .withInputProps({
          isClearable: !activityStatus?.HasItinerary || ReconciliationEditStatuses.includes(activityStatus?.Status),
          readOnly: activityStatus?.HasItinerary && !ReconciliationEditStatuses.includes(activityStatus?.Status)
        })
        .isNumeric(),

      fieldHelper
        .selectInputQueryItems('RequestedById', {
          searchUrl: `${ApiEndpoints.selectOptions.companies.companyContactsList(companyId, false)}`,
          enabled: companyId != undefined
        })
        .withInputProps({
          isClearable: true
        })
        .isNumeric(),

      fieldHelper
        .selectInputQueryItems('ApprovedBookerId', {
          searchUrl: `${ApiEndpoints.selectOptions.companies.companyContactsList(companyId, true)}`,
          enabled: companyId != undefined
        })
        .withInputProps({
          isClearable: true
        })
        .withRenderIf(
          () =>
            !(
              approvalEnabled ??
              ((activityStatus?.ObtApprovalsEnabled ?? false) || (activityStatus?.AgentApprovalsEnabled ?? false))
            )
        )
        .isNumeric(),
      CompanyContactsSearchField(
        fieldHelper,
        'TravelerIds',
        {
          companyId,
          approvedBookerOnly: false,
          hotelBookingType,
          initialTravelers,
          companyContactTypes: [CompanyContactType.StandardTraveller, CompanyContactType.GroupsOnlyTraveller]
        },
        {},
        {
          activityId,
          companyId,
          onCreated: onNewTravellerCreated
        }
      )
        .withInputProps({
          disableSort: true,
          readOnly: activityStatus?.HasItinerary && !ReconciliationEditStatuses.includes(activityStatus?.Status),
          components: {
            MultiValueLabel: (props: any) => {
              // Find the correct traveler data from viewModel instead of using props.data
              const correctTravelerData =
                viewModel?.InitialTravellerSelectOptions?.find((t) => t.Value === props.data.Value) || props.data;

              // Create a stable key based on the current traveler list and their primary status
              const travellerListKey =
                viewModel?.InitialTravellerSelectOptions?.map(
                  (t) => `${t.Value}-${t.Metadata?.find((m) => m.Key === 'isPrimary')?.Value || false}`
                ).join('|') || 'empty';

              return (
                <SelectedTravellerPill
                  key={`${props.data.Value}-${travellerListKey}`}
                  selectOption={correctTravelerData}
                  activityId={activityId}
                />
              );
            },
            MultiValueRemove: (props) => {
              if (props.selectProps.isDisabled) {
                return <></>;
              }

              return (
                <CloseButton
                  height={'30px'}
                  size={'sm'}
                  onClick={async (e) => {
                    if (props.innerProps.onClick) {
                      // Show warning toast with longer duration (8 seconds)
                      toast.warningToast(
                        'Traveler Removed',
                        `${props.data.Label} has been removed. All related coding and reporting requirements for this traveler will also be removed.`,
                        8000
                      );

                      // Trigger the removal
                      props.innerProps.onClick(e as any);

                      // Invalidate queries after traveler deletion to update primary traveler
                      setTimeout(async () => {
                        if (activityId) {
                          // Invalidate activity status query to update traveler select options
                          await queryInvalidator.invalidateStatusQuery();

                          // Refetch edit activity query to update InitialTravellerSelectOptions with new primary traveler
                          await queryClient.refetchQueries({
                            queryKey: [QueryKeys.editActivity(activityId), ActivityEndpoints.edit.get(activityId)],
                            type: 'all'
                          });
                        }
                      }, 100);
                    }
                  }}
                />
              );
            }
          }
        })
        .withHelpText(
          <Box>
            <Text>
              <strong>The star ⭐ denotes the Primary</strong> <br /> Changing the primary traveler:
            </Text>
            <Text mt={1}>• Click on any circle in a traveler pill to promote that traveler to primary</Text>
            <Text>• Use the "Make Primary" button in the Travelers & Payment Information section below</Text>
          </Box>
        )
        .withValidation({
          required: {},
          validate: (_, formValues) => {
            if (!formValues.TravelerIds || formValues.TravelerIds.length === 0) {
              return 'At least one traveller is required';
            }
            return undefined;
          }
        }),
      // Note: GuestTravelerNames is a custom field rendered separately in the form components
      // because it requires special handling (displays count 0-20, stores string[] like ["Guest 1", "Guest 2"])
      fieldHelper
        .dateRangeInput('SelectedDates', {
          startDate: 'CheckInDate',
          endDate: 'CheckOutDate',
          disableSameDaySelection: true
        })
        .withRenderUnder(
          !checkOutDate || !checkInDate ? undefined : (
            <Text fontSize={'small'} m={'-4px 0 0 0'} paddingLeft={'2px'} fontWeight={'bold'} fontStyle={'italic'}>
              {getTotalNights(checkInDate, checkOutDate)} nights
            </Text>
          )
        )
        .withHelpText(
          activityStatus?.HasSelectedHotelRates
            ? 'Remove hotel rates to change the travel dates'
            : 'Cannot change value once rates are added or an itinerary has been sent'
        )
        .withInputProps({
          readOnly:
            !ReconciliationEditStatuses.includes(activityStatus?.Status ?? ActivityStatus.None) &&
            (activityStatus?.HasSentItinerary || activityStatus?.HasSelectedHotelRates),
          minDate: ReconciliationEditStatuses.includes(activityStatus?.Status ?? ActivityStatus.None)
            ? undefined
            : new Date()
        })
        .withValidation({
          validate: (_, values) => {
            return validateTravelDates(values.CheckInDate, values.CheckOutDate, activityStatus?.HasBookedHotel);
          }
        })
        .withLabel('Travel Dates'),
      fieldHelper.textInput('Comments'),
      fieldHelper
        .textArea('ApproverNotes')
        .withLabel('Approver Notes')
        .withInputProps({ rows: 3 })
        .withRenderIf(
          () =>
            approvalEnabled ??
            ((activityStatus?.ObtApprovalsEnabled ?? false) || (activityStatus?.AgentApprovalsEnabled ?? false))
        ),
      fieldHelper.richTextInput('AdditionalHotelNotes'),
      isNewActivityForm
        ? fieldHelper.textInput('EstimatedTimeOfArrival').withValidation({ required: {} })
        : fieldHelper.textInput('EstimatedTimeOfArrival'),
      fieldHelper.textInput('AreaAmenities'),
      fieldHelper.textInput('PhoneNumber').withValidation({ required: {} }),
      fieldHelper.textInput('Email'),
      fieldHelper.textInput('ConfirmationVia').withValidation({ required: {} }),
      fieldHelper
        .textInput('PreferredCityFreeText')
        .withLabel('Requested City Free Text')
        .withRenderIf(() => cityHotelFreeText)
        .withRenderUnder(
          <ClickableText
            boxProps={{ mt: '-4px' }}
            onClick={() => {
              formMethods.setValue('PreferredHotelFreeText', undefined);
              formMethods.setValue('PreferredCityFreeText', undefined);
              setCityHotelFreeText(false);
            }}>
            Switch to city/hotel search
          </ClickableText>
        ),

      fieldHelper
        .textInput('PreferredHotelFreeText')
        .withLabel('Requested Hotel Free Text')
        .withRenderIf(() => cityHotelFreeText),

      fieldHelper
        .selectInputQuerySearchItems('PreferredCityId', {
          searchUrl: ApiEndpoints.selectOptions.cities(),
          minSearchChars: 3,
          initialOptions: viewModel?.InitialPreferredCitySelectOption
        })
        .withLabel('Requested City')
        .withInputProps({ isClearable: true })
        .withRenderUnder(
          <ClickableText
            boxProps={{ mt: '-4px' }}
            onClick={() => {
              formMethods.setValue('PreferredHotelId', undefined);
              formMethods.setValue('PreferredCityId', undefined);
              setCityHotelFreeText(true);
            }}>
            Can't find your city or hotel? Click here for free text input
          </ClickableText>
        )
        .withRenderIf(() => !cityHotelFreeText),

      fieldHelper
        .selectInputQueryItems('PreferredHotelId', {
          searchUrl: ApiEndpoints.selectOptions.hotels.hotelInCity(cityId ?? 0),
          enabled: cityId !== undefined && cityId > 0
        })
        .withLabel('Requested Hotel')
        .withInputProps({ isClearable: true })
        .withRenderIf(() => !cityHotelFreeText),

      fieldHelper
        .selectInputQueryItems('CompanyConfirmationId', {
          searchUrl: ApiEndpoints.selectOptions.companies.companyRelatedCompanies(companyId)
        })
        .withLabel('Confirm Company')
        .withInputProps({ isClearable: true })
        .withRenderIf(
          () =>
            companyId !== undefined &&
            companyId > 0 &&
            (companyRelatedCompanies.data?.RelatedCompanies?.length ?? 0) > 0
        )
        .withValidation({
          validate: (value, methods) => {
            if (!value) {
              return 'Please confirm the company for this booking';
            }
            if (value != methods.CompanyId) {
              return 'Company confirmation must match the selected company';
            }
            return undefined;
          }
        })
        .isNumeric()
    ];
  }, [
    activityId,
    companyId,
    companyDivisionId,
    divisions,
    hotelBookingType,
    cityId,
    fieldHelper,
    viewModel,
    cityHotelFreeText,
    companyConfirmationId,
    activityStatus?.HasItinerary,
    activityStatus?.Status,
    activityStatus?.HasSelectedHotelRates,
    activityStatus?.HasSentItinerary,
    initialTravelers,
    checkInDate,
    checkOutDate,
    activityStatus?.AgentApprovalsEnabled,
    isDisabled,
    approvalEnabled
  ]);
};
